import { resolveFormTenantId } from "../../utils/tenantContext";

import { zodResolver } from '@hookform/resolvers/zod';
import { getStandardizedSignatures } from '../../utils/formHelpers';
import React, { useEffect, useState } from 'react';
import { getLocalSystemDate, parseIsoDateToMDYUS, toIsoDate } from '../../utils/dateUtils';
import { mergeFormDataWithStatusExtras } from '../../utils/applicationFormPayload';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { request } from '../../utils/api';
import EvaluationFormHeader from '../EvaluationNew/components/EvaluationFormHeader';
import EvaluationInput from '../EvaluationNew/components/EvaluationInput';
import InputDatePicker from '../EvaluationNew/components/InputDatePicker';
import { employeeHealthExaminationSchema, formatPhone, formatName } from './application.validation';
import ApplicationFormFooter from './ApplicationFormFooter';
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import { CheckIcon, PlusIcon, DeleteIcon } from "../../Icons";

const EmployeeHealthExamination = ({ positionName, onClose = () => { } }) => {
  const [examinationId, setExaminationId] = useState(null);

  const methods = useForm({
    resolver: zodResolver(employeeHealthExaminationSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      employeeName: "",
      position: positionName || "",
      patientTitle: "",
      patientName: "",
      conditionException: "",
      noHazardStatement: "",
      height: "",
      weight: "",
      bloodPressure: "",
      pulse: "",
      ppdTestResult: "",
      mantoux: "",
      ppdDateAdministered: "",
      chestXrayResult: "",
      chestXrayDateAdministered: "",
      adenopathy: "",
      reflexes: "",
      eyes: "",
      hearing: "",
      nose: "",
      throat: "",
      tongue: "",
      teeth: "",
      abdomen: "",
      rectal: "",
      heartSize: "",
      murmur: "",
      rhythm: "",
      arteries: "",
      chestBreathSounds: "",
      resonance: "",
      physicianName: "",
      physicianPhone: "",
      physicianAddress: "",
      physicianSignature: "",
      physicianDate: "",
      applicantSignature: "",
      applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
      familyHistories: [],
      positionAppliedFor: positionName || "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: ""
    },
    mode: "onChange"
  });


  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const merged = mergeFormDataWithStatusExtras(data, additionalData);
      const payload = {
        ...merged,
        items: (merged.items || []).map((item) => ({
          ...item,
          dateDiscussed: item.dateDiscussed ? toIsoDate(item.dateDiscussed) : "",
        })),
        ppdDateAdministered: merged.ppdDateAdministered ? toIsoDate(merged.ppdDateAdministered) : "",
        chestXrayDateAdministered: merged.chestXrayDateAdministered ? toIsoDate(merged.chestXrayDateAdministered) : "",
        physicianDate: merged.physicianDate ? toIsoDate(merged.physicianDate) : "",
        applicantSignedAt: toIsoDate(merged.applicantSignedAt),
        reviewedBySignedAt: toIsoDate(merged.reviewedBySignedAt),
        flag: actualFlag,
      };

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      const method = examinationId ? 'PUT' : 'POST';
      const url = examinationId ? `/employee-health-examinations/${examinationId}` : '/employee-health-examinations';

      const response = await request(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.status === 1) {
        toast.success(response.message || "Health Examination processed successfully!");
        closeForm()
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,

    actualFromId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "employeeName",
    applicantSignatureDateField: "applicantSignedAt",
    applicantSignatureField: "applicantSignature",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  const { handleSubmit, setValue, watch, control, reset, formState: { errors } } = methods;

  const { fields: familyFields, append: appendFamily, remove: removeFamily } = useFieldArray({
    control,
    name: "familyHistories"
  });

  useEffect(() => {
    if (positionName) {
      setValue("positionAppliedFor", positionName);
      setValue("position", positionName);
    }
  }, [positionName, setValue]);

  useEffect(() => {
    const fetchHealthExamination = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId) return;

      try {
        if (!actualFromId && !targetUserId && !examinationId) {
          if (currentUserData && !isAdmin) {
            methods.reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              employeeName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              position: positionName || "",
              positionAppliedFor: positionName || "",
              applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
              familyHistories: []
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/employee-health-examinations/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/employee-health-examinations?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/employee-health-examinations/${actualFromId || targetUserId}`);
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) data = response.result[0];
          } else if (response.result?.examination) {
            data = response.result.examination;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            if (response.result.items.length > 0) {
              data = response.result.items.find(item => item.userId === Number.parseInt(targetUserId));
            }
          }
        }

        if (data) {
          setExaminationId(data.id);
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            employeeName: data.employeeName || data.applicantName || "",
            ppdDateAdministered: parseIsoDateToMDYUS(data.ppdDateAdministered) || "",
            chestXrayDateAdministered: parseIsoDateToMDYUS(data.chestXrayDateAdministered) || "",
            physicianDate: parseIsoDateToMDYUS(data.physicianDate) || "",
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).applicantSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).reviewedBySignedAt || ""),
            familyHistories: data.familyHistories || []
          });
        }
      } catch (error) {
        console.error("Error fetching health examination:", error);
      }
    };
    fetchHealthExamination();
  }, [targetUserId, reset, currentUserData, isAdmin, methods, actualFromId, examinationId, positionName]);





  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader title="Employee Health Examination" />
        <div className="p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <CorrectionRequestAlert status={currentStatus} comments={methods.watch("comments")} />
              <fieldset disabled={isReadOnly} className="space-y-8">
                {/* Employee Info */}
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-6">
                  <h3 className="font-bold text-gray-900 border-b border-gray-200 pb-3 mb-4 uppercase tracking-wide text-sm">
                    Employee Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <EvaluationInput
                      name="employeeName"
                      label="Employee Name"
                      onInput={(e) => { e.target.value = formatName(e.target.value); }}
                    />
                    <EvaluationInput name="position" label="Position" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <EvaluationInput
                      name="patientTitle"
                      label="Title (Mr/Mrs/Ms)"
                    />
                    <div className="md:col-span-2">
                      <EvaluationInput
                        name="patientName"
                        label="Patient Name (Employee)"
                        onInput={(e) => { e.target.value = formatName(e.target.value); }}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-white border boundary-gray-200 p-6 rounded-xl shadow-sm">
                    <h3 className="font-bold text-gray-900 border-b border-gray-200 pb-3 mb-2 flex items-center justify-between">
                      <span>Family History</span>
                      <button
                        type="button"
                        onClick={() =>
                          appendFamily({
                            familyMember: "",
                            illness: "",
                            relationship: "",
                          })
                        }
                        className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-full hover:bg-blue-100 transition-colors flex items-center gap-1"
                      >
                        <PlusIcon className="text-[10px]" /> Add History
                      </button>
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      List any family history of illness (Diabetes, Tuberculosis,
                      Epilepsy, Heart Disease, Kidney Disease, Cancer, Mental
                      Disorder, etc.)
                    </p>

                    <div className="space-y-3">
                      {familyFields.map((field, index) => (
                        <div
                          key={field.id}
                          className="flex flex-col md:flex-row gap-4 items-end bg-gray-50 p-4 rounded-lg border border-gray-100 relative group"
                        >
                          <div className="flex-1 w-full md:w-auto">
                            <EvaluationInput
                              name={`familyHistories.${index}.familyMember`}
                              label="Family Member"
                              placeholder="e.g. Father"
                            />
                          </div>
                          <div className="flex-1 w-full md:w-auto">
                            <EvaluationInput
                              name={`familyHistories.${index}.illness`}
                              label="Illness"
                              placeholder="e.g. Diabetes"
                            />
                          </div>
                          <div className="flex-1 w-full md:w-auto">
                            <EvaluationInput
                              name={`familyHistories.${index}.relationship`}
                              label="Relationship"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFamily(index)}
                            className="absolute top-2 right-2 md:static md:mb-2 text-gray-400 hover:text-red-500 transition-colors p-2"
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      ))}
                      {familyFields.length === 0 && (
                        <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-gray-500 text-sm">
                          No family history added. Click "Add History" to add
                          record.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Physical Examination */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <div className="bg-blue-50/50 rounded-xl border border-blue-100 overflow-hidden">
                    <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
                      <h3 className="font-bold text-blue-900 text-lg">
                        Physical Examination
                      </h3>
                      <p className="text-blue-600 text-sm">
                        To be completed by Physician
                      </p>
                    </div>

                    <div className="p-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
                        <EvaluationInput name="height" label="Height" />
                        <EvaluationInput name="weight" label="Weight" />
                        <EvaluationInput
                          name="bloodPressure"
                          label="Blood Pressure"
                        />
                        <EvaluationInput name="pulse" label="Pulse" />
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="space-y-6">
                          <h4 className="font-bold text-gray-800 border-b border-gray-200 pb-2">
                            Tests
                          </h4>
                          <div className="grid grid-cols-1 gap-6 bg-white p-4 rounded-lg border border-gray-100">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700">
                                  PPD Test Result <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-4 pt-1">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      value="Positive"
                                      {...methods.register("ppdTestResult")}
                                      className="w-4 h-4 text-blue-600"
                                    />
                                    <span className="text-sm">Positive</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      value="Negative"
                                      {...methods.register("ppdTestResult")}
                                      className="w-4 h-4 text-blue-600"
                                    />
                                    <span className="text-sm">Negative</span>
                                  </label>
                                </div>
                                {errors.ppdTestResult && (
                                  <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.ppdTestResult.message}</p>
                                )}
                              </div>
                              <EvaluationInput
                                name="mantoux"
                                label="Mantoux (mm)"
                                type="number"
                              />
                            </div>

                            <InputDatePicker
                              formatSeparator="/"
                              name="ppdDateAdministered"
                              label="Date Administered"
                              max={getLocalSystemDate()}
                            />

                            <div className="border-t border-dashed border-gray-200 my-2"></div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium text-gray-700">
                                  Chest X-Ray Result <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-4 pt-1">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      value="Positive"
                                      {...methods.register("chestXrayResult")}
                                      className="w-4 h-4 text-blue-600"
                                    />
                                    <span className="text-sm">Positive</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      value="Negative"
                                      {...methods.register("chestXrayResult")}
                                      className="w-4 h-4 text-blue-600"
                                    />
                                    <span className="text-sm">Negative</span>
                                  </label>
                                </div>
                                {errors.chestXrayResult && (
                                  <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.chestXrayResult.message}</p>
                                )}
                              </div>
                              <InputDatePicker
                                formatSeparator="/"
                                name="chestXrayDateAdministered"
                                label="Date Administered"
                                max={getLocalSystemDate()}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-6">
                          <h4 className="font-bold text-gray-800 border-b border-gray-200 pb-2">
                            Physical Findings (Normal/Abnormal)
                          </h4>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-4 bg-white p-4 rounded-lg border border-gray-100">
                            <EvaluationInput name="eyes" label="Eyes" />
                            <EvaluationInput name="hearing" label="Hearing" />
                            <EvaluationInput name="nose" label="Nose" />
                            <EvaluationInput name="throat" label="Throat" />
                            <EvaluationInput name="teeth" label="Teeth" />
                            <EvaluationInput name="tongue" label="Tongue" />
                            <EvaluationInput name="adenopathy" label="Adenopathy" />
                            <EvaluationInput
                              name="chestBreathSounds"
                              label="Chest/Breath Sounds"
                            />
                            <EvaluationInput name="heartSize" label="Heart Size" />
                            <EvaluationInput name="rhythm" label="Rhythm" />
                            <EvaluationInput name="abdomen" label="Abdomen" />
                            <EvaluationInput name="rectal" label="Rectal" />
                            <EvaluationInput name="reflexes" label="Reflexes" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Physician Info */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="bg-blue-100 text-blue-600 p-1.5 rounded-lg">
                        <CheckIcon className="h-5 w-5" />
                      </span>
                      Physician Certification
                    </h3>

                    <div className="space-y-4 mb-6">
                      <EvaluationInput
                        name="conditionException"
                        label="Is patient free from communicable disease?"
                        placeholder="Yes/No or Exception details"
                      />
                      <EvaluationInput
                        name="noHazardStatement"
                        label="Statement of Qualification"
                        placeholder="e.g. Patient is physically and mentally capable of performing duties..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                      <EvaluationInput
                        name="physicianName"
                        label="Physician Name"
                        onInput={(e) => { e.target.value = formatName(e.target.value); }}
                      />
                      <EvaluationInput
                        name="physicianPhone"
                        label="Physician Phone"
                        placeholder="(XXX)XXX-XXXX"
                        maxLength={13}
                        onInput={(e) => { e.target.value = formatPhone(e.target.value); }}
                      />
                      <div className="md:col-span-2">
                        <EvaluationInput
                          name="physicianAddress"
                          label="Physician Address"
                        />
                      </div>
                      <EvaluationInput
                        name="physicianSignature"
                        label="Physician Signature"
                        placeholder="Type name to sign"
                      />
                      <InputDatePicker
                        formatSeparator="/"
                        name="physicianDate"
                        label="Date"
                        max={getLocalSystemDate()}
                      />
                    </div>
                  </div>
                </div>

                <ApplicationFormFooter
                  page={1}
                  totalPages={1}
                  methods={methods}
                  onSave={handleSave}
                  onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                  onStatusUpdate={handleStatusAction}
                  isSignatureModalOpen={isSignatureModalOpen}
                  openSignatureModal={openSignatureModal}
                  closeSignatureModal={closeSignatureModal}
                  handleSignatureApply={handleSignatureApply}
                  signerName={signerName}
                  applicantName={watch("employeeName") || ""}
                  applicantSignature={watch("applicantSignature")}
                  applicantSignedAt={watch("applicantSignedAt")}
                  reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                  reviewedBySignature={watch("reviewedBySignature")}
                  reviewedBySignedAt={watch("reviewedBySignedAt")}
                  showCertification={false}
                  isReadOnly={isReadOnly}
                  onClose={closeForm}
                  currentStatus={currentStatus}
                  submitLabel="Submit & Print"
                />
              </fieldset>
            </form>


          </FormProvider>
        </div>

      </div>
    </>
  );
};

export default EmployeeHealthExamination;

